import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { settings, menu } from '../../../constants'
import { useAuth } from '../../../context/AuthContext'
import sty from './Header.module.css'

const Header = () => {
    const navigate = useNavigate()
    const { loginUser, setLoginUser, role, setRole } = useAuth()
    const [isDrawerOpen, setIsDrawerOpen] = useState(false)
    const location = useLocation()
    const messageRef = useRef(null)
    // 既存のステート変数を維持
    const [isSubmenuOpen, setIsSubmenuOpen] = useState(false); // 追加: サブメニューの開閉状態
    
    // ロゴのパス設定
    const getLogoPath = () => {
        const currentPath = location.pathname;
        
        if (currentPath.startsWith('/mng')) {
            return menu.MNG.HOME.PATH;
        }
        if (currentPath.startsWith('/share')) {
            return menu.SHARE.ENGINEER_LIST.PATH;
        }
        return menu.ENGINEER.HOME.PATH;
    };
    const logoHomePath = getLogoPath();

    // サブメニューの開閉を切り替える関数
    const toggleSubmenu = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsSubmenuOpen(!isSubmenuOpen);
    };

    // マイページのタブ項目を定義
    const myPageTabs = [
        { name: 'プロフィール', path: `${menu.ENGINEER.HOME.PATH}?tab=tab1` },
        { name: '保有資格', path: `${menu.ENGINEER.HOME.PATH}?tab=tab2` },
        { name: 'プロジェクト', path: `${menu.ENGINEER.HOME.PATH}?tab=tab3` },
        { name: 'スキルシート', path: `${menu.ENGINEER.HOME.PATH}?tab=tab4` },
        { name: '実績', path: `${menu.ENGINEER.HOME.PATH}?tab=tab5` }
    ];

    // ログイン時のヘッダーの情報を非表示にするパス
    const HIDDEN_HEADER_INFO_PATHS = [
        menu.ENGINEER.SIGNIN.PATH,    // エンジニアログイン
        menu.MNG.SIGNIN.PATH,         // 管理者ログイン
        '/signup',                    // エンジニアのアカウント登録
        '/authentication',            // 認証コード入力
        '/forgot-password',           // パスワード忘れ
        '/guide',                     // 利用規約
        '/privacy',                   // プライバシーポリシー
        '/password-reset',            // パスワードリセット
        '/share',                     // Web経歴書
        '/private',                   // Web経歴書(非公開)
        '/server-error',              // 500エラー
        '*'                           // 404エラー
    ]
    const isHiddenHeaderInfo = HIDDEN_HEADER_INFO_PATHS.some(path => {
        // パスパラメータがある場合
        if (path === '/share') {
            return location.pathname.startsWith('/share');
        }
        if (path === '/password-reset') {
            return location.pathname.startsWith('/password-reset');
        }
        return location.pathname === path;
    });

    // リロード(useAuth無、cookieのトークン有)
    useLayoutEffect(() => {
        /*  cookieにトークンが存在する場合、ヘッダーに必要な情報取得
            httpOnly=trueのため、cookie取得できない。
            そのためトークン有の場合にログイン画面遷移しないことを利用。*/
        (async () => {
            // ログイン画面など特定のパスでは header-info API を呼び出さない
            if (isHiddenHeaderInfo) {
                return;
            }
            
            try {
                const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/header-info`, {
                    credentials: 'include',
                })
                const result = await response.json()
                if (response.status === 200 && result.status === 'OK') {
                    setLoginUser(result.data.name)
                    setRole(result.data.role)
                } else {
                    setLoginUser(null)
                    setRole(null)
                }
            } catch (e) {
                setLoginUser(null)
                setRole(null)
            }
        })()
    }, [loginUser, role, isHiddenHeaderInfo, location.pathname])

    // ドロワー開閉処理
    const toggleDrawer = () => {
        setIsDrawerOpen(!isDrawerOpen)
    }

    // ドロワー外クリックの検知
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isDrawerOpen && !messageRef.current?.contains(event.target)) {
                setIsDrawerOpen(false)
            }
        }
        document.addEventListener('click', handleClickOutside)
        return () => {
            document.removeEventListener('click', handleClickOutside)
        }
    }, [isDrawerOpen])

    // ログアウト処理
    const handleLogout = async () => {
        // ログアウト後の画面遷移
        let signinPath;
        if(role === settings.MNG.ROLE){
            signinPath = menu.MNG.SIGNIN.PATH
        }else{
            signinPath = menu.ENGINEER.SIGNIN.PATH
        }

        try {
            const logoutEvent = new CustomEvent('app-logout');
            window.dispatchEvent(logoutEvent);
            
            // 少し待機して保存が完了するのを待つ
            await new Promise(resolve => setTimeout(resolve, 100));

            // ログアウトAPIを呼び出し
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/logout`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                credentials: 'include'
            });
            
            const result = await response.json();
            if (result.status === 'OK') {
                setLoginUser(null);
                setRole(null);
                localStorage.removeItem('userId');
                localStorage.removeItem('login_start_time');
            }
            navigate(signinPath);
        }catch(e){
            navigate(signinPath);
        }
    }

    return (
        <header>
            <Link to={logoHomePath}>
                <img className={sty['header__logo']} src="/images/logo.svg" alt="ロゴ" />
            </Link>
            
            {!isHiddenHeaderInfo && (
                <>
                    {/* PC版 */}
                    <div className={sty['header__pc-content']}>
                        <div className={sty['header__links']}>
                        {role === settings.MNG.ROLE && (
                            <>  {/* 管理者 */}
                                <Link to={menu.MNG.HOME.PATH} className={`${sty['header__link']} ${location.pathname === menu.MNG.HOME.PATH ? sty['active-link'] : ''}`}>
                                    {menu.MNG.HOME.PAGE_NAME}
                                </Link>
                                <Link to={menu.MNG.SETTINGS.PATH} className={`${sty['header__link']} ${location.pathname === menu.MNG.SETTINGS.PATH ? sty['active-link'] : ''}`}>
                                    {menu.MNG.SETTINGS.PAGE_NAME}
                                </Link>
                            </>
                            )}
                            {role === settings.ENGINEER.ROLE && (
                            <>  {/* エンジニア */}
                                <Link to={menu.ENGINEER.HOME.PATH} className={`${sty['header__link']} ${location.pathname === menu.ENGINEER.HOME.PATH ? sty['active-link'] : ''}`}>
                                    {menu.ENGINEER.HOME.PAGE_NAME}
                                </Link>
                                <Link to={menu.ENGINEER.ENGINEER_LIST.PATH} className={`${sty['header__link']} ${location.pathname === menu.ENGINEER.ENGINEER_LIST.PATH ? sty['active-link'] : ''}`}>
                                    {menu.ENGINEER.ENGINEER_LIST.PAGE_NAME}
                                </Link>
                                <Link to={menu.ENGINEER.SETTINGS.PATH} className={`${sty['header__link']} ${location.pathname === menu.ENGINEER.SETTINGS.PATH ? sty['active-link'] : ''}`}>
                                    {menu.ENGINEER.SETTINGS.PAGE_NAME}
                                </Link>
                            </>
                            )}
                        </div>
                        <div className={sty['header__user-info']}>
                            <p className={`${sty['header__message']} ${sty['header__message-pc']}`}>
                                {loginUser ? loginUser : ''}
                            </p>
                            <button className={`${sty['logout-button']} ${sty['logout-button-pc']}`} onClick={handleLogout}>ログアウト</button>
                        </div>
                    </div>
                
                    {/* SP版 */}
                    <div className={sty['header__sp-content']}>
                        <div className={sty['header__user-info-sp']}>
                            <p className={sty['header__message']} ref={messageRef} onClick={toggleDrawer}>
                                {loginUser ? loginUser : ''}
                            </p>
                        </div>
                        {/* ドロワー */}
                        { isDrawerOpen && (
                            <div className={`${sty['header__drawer']} ${isDrawerOpen ? sty['header__drawer-open'] : ''}`}>
                                <ul>
                                {role === settings.MNG.ROLE && (
                                    <>
                                        <li className={`${sty['header__drawer-link']} ${sty['header__message-drawer-home-link']}`}>
                                            <Link to={menu.MNG.HOME.PATH}>
                                                {menu.MNG.HOME.PAGE_NAME}
                                            </Link>
                                        </li>
                                        <li className={`${sty['header__drawer-link']} ${sty['header__message-drawer-account-link']}`}>
                                            <Link to={menu.MNG.SETTINGS.PATH}>
                                                {menu.MNG.SETTINGS.PAGE_NAME}
                                            </Link>
                                        </li>
                                    </>
                                )}
                                {role === settings.ENGINEER.ROLE && (
                                    <>
                                        <li className={`${sty['header__drawer-link']} ${sty['header__message-drawer-home-link']}`}>
                                            <Link to="#" onClick={toggleSubmenu}>
                                                {menu.ENGINEER.HOME.PAGE_NAME}
                                            </Link>
                                            <ul className={`${sty['header__drawer-submenu']} ${isSubmenuOpen ? sty['header__drawer-submenu-open'] : ''}`}>
                                                {myPageTabs.map((tab, index) => (
                                                    <li key={index}>
                                                        <Link 
                                                            to={tab.path}
                                                            onClick={() => {
                                                                setIsDrawerOpen(false);
                                                                setIsSubmenuOpen(false);
                                                            }}
                                                        >
                                                            {tab.name}
                                                        </Link>
                                                    </li>
                                                ))}
                                            </ul>
                                        </li>
                                        <li className={`${sty['header__drawer-link']} ${sty['header__message-drawer-account-link']}`}>
                                            <Link to={menu.ENGINEER.ENGINEER_LIST.PATH}>
                                                {menu.ENGINEER.ENGINEER_LIST.PAGE_NAME}
                                            </Link>
                                        </li>
                                        <li className={`${sty['header__drawer-link']} ${sty['header__message-drawer-account-link']}`}>
                                            <Link to={menu.ENGINEER.SETTINGS.PATH}>
                                                {menu.ENGINEER.SETTINGS.PAGE_NAME}
                                            </Link>
                                        </li>
                                    </>
                                    )}
                                    <li className={`${sty['header__drawer-link']} ${sty['header__message-drawer-logout-link']}`}>
                                        <Link onClick={() => handleLogout()} >
                                            ログアウト
                                        </Link>
                                    </li>
                                </ul>
                            </div>
                        )}
                    </div>
                </>
            )}
        </header>
    )
}

export default Header
